<template>
  <v-row justify="center">
    <v-col style="max-width: 540px !important">
      <v-row no-gutters justify="start" v-if="$store.state.payments.retrieveLoader">
        <v-col cols="12">
          <v-boilerplate type="image, article" />
          <v-skeleton-loader />
        </v-col>
      </v-row>
      <v-card v-else min-width="350">
        <template v-if="payment.account.logo">
          <v-card-title class="py-4 text-center">
            <v-spacer />
            <v-sheet rounded outlined>
              <v-responsive class="align-center" :aspect-ratio="1/1">
                <v-sheet class="px-1" width="100">
                  <v-img :src="payment.account.logo" width="100%" />
                </v-sheet>
              </v-responsive>
            </v-sheet>
            <v-spacer />
          </v-card-title>
          <v-divider />
        </template>
        <v-card-subtitle>
          <v-toolbar class="mx-n2" color="transparent" flat height="18">
            <v-toolbar-items>
              <v-btn @click="$router.push({ name: 'PaymentRetrieve' })" :disabled="loaderPay" text color="primary"><v-icon left>mdi-arrow-left</v-icon>Volver</v-btn>
            </v-toolbar-items>
            <v-toolbar-title class="pl-4">
              <span class="body-1 text--secondary font-weight-medium">{{payment.balance | currency(payment.currency)}} {{payment.currency}}<v-icon color="secondary">mdi-circle-small</v-icon>{{payment.description}}</span>
            </v-toolbar-title>
          </v-toolbar>
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="background">
          <v-alert v-if="gatewayError" ref="alert" class="" type="error" border="left" text dense>{{gatewayError}}</v-alert>
          <v-row v-if="processing" no-gutters justify="center">
            <v-col cols="12" class="text-center py-4">
              <v-icon color="primary" size="40">mdi-clock-outline</v-icon>
              <span class="d-block mt-3 body-1 font-weight-medium">Tu pago está siendo procesado</span>
              <span class="d-block mt-1 text--secondary">Mercado Pago está revisando la transacción. No vuelvas a pagar: te confirmaremos por correo en unos minutos.</span>
            </v-col>
          </v-row>
          <template v-else>
            <!-- The form stays mounted and is only made transparent while it
                 loads: both children mount third party iframes, which need real
                 dimensions to render. Hiding them with v-if or display:none
                 would bring them up sized at zero. -->
            <div class="mp-form" :class="{ 'mp-form--loading': loadingForm }">
              <v-skeleton-loader v-if="loadingForm" class="mp-form__skeleton" type="list-item-two-line, list-item-two-line, list-item-two-line" />
              <div :class="{ 'mp-form__content--loading': loadingForm }">
                <mercadopago-brick v-if="experience === 'brick' && publicKey" ref="form" :amount="payment.balance" :payer-email="payerEmail" :primary-color="payment.account.color_background" :public-key="publicKey" @error="showError" @ready="formReady = true" />
                <mercadopago-secure-fields v-else-if="publicKey" ref="form" :public-key="publicKey" @error="showError" @ready="formReady = true" />
              </div>
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0 background" v-if="!processing && publicKey">
          <v-row align="center" justify="start">
            <v-col cols="12">
              <v-btn @click="pay" :style="{'background-color': `${payment.account.color_background} !important`}" :disabled="loaderPay || !formReady" :loading="loaderPay" height="40" block dark>Pagar</v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
      <piriod-sign />
    </v-col>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import GatewayViewMixin from '@/mixins/GatewayViewMixin'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import MercadopagoBrick from '@/components/MercadopagoBrick'
import MercadopagoSecureFields from '@/components/MercadopagoSecureFields'
import PiriodSign from '@/components/PiriodSign'
import { resolveMercadopagoExperience } from '@/helpers/mercadopago'

export default {
  name: 'piriod-gateway-mercadopago',
  inject: ['theme'],
  components: {
    MercadopagoBrick,
    MercadopagoSecureFields,
    PiriodSign,
    VBoilerplate: {
      functional: true,
      render (h, { data, props, children }) {
        return h('v-skeleton-loader', {
          ...data,
          props: {
            boilerplate: true,
            elevation: 2,
            ...props
          }
        }, children)
      }
    }
  },
  mixins: [
    GatewayViewMixin,
    GenericViewMixin
  ],
  data: () => ({
    formReady: false,
    processing: false
  }),
  computed: {
    ...mapState({
      payment: state => cloneDeep(state.payments.payDetail),
      source: state => cloneDeep(state.payments.sourcesDetail),
      paymentsDetail: state => state.payments.paymentsDetail
    }),
    loadingForm () {
      // An error raised before the form ever became ready (a dead SDK, a
      // missing key) must clear the skeleton, or it covers the alert that
      // explains what went wrong.
      return !this.formReady && !this.gatewayError
    },
    experience () {
      // Stage 2 will also pass
      //   provider: (this.payment.gateway_options || {}).mercadopago ...
      // and nothing else here changes.
      return resolveMercadopagoExperience({ query: this.$route.query.experience })
    },
    payerEmail () {
      return (this.payment.customer && this.payment.customer.email) || ''
    },
    publicKey () {
      return (this.payment.merchants && this.payment.merchants.mercadopago) || ''
    }
  },
  created () {
    this.$store.dispatch('payments/RETRIEVE', {
      resource: 'pay',
      id: this.$route.params.id
    })
    .then(() => {
      if (this.payment.status === 'succeeded' || !this.payment.gateways.includes('mercadopago')) {
        this.$router.push({ name: 'PaymentRetrieve' })
      }
    })
  },
  methods: {
    /**
     * Surface an error where the payer will actually see it.
     *
     * The alert renders at the top of the card, which may be scrolled out of
     * view on a long form, so bring it back into sight.
     */
    showError (message) {
      this.gatewayError = message
      if (!message) {
        return
      }
      this.$nextTick(() => {
        const alert = this.$refs.alert
        if (alert && alert.$el && alert.$el.scrollIntoView) {
          alert.$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    /**
     * Single entry point for both experiences: the child validates and
     * tokenizes, the container runs the payment chain.
     */
    async pay () {
      // loaderPay drives the button from the first click, so the card data
      // never sits tokenizing with the button still looking idle.
      this.loaderPay = true
      try {
        this.showError('')
        const token = await this.$refs.form.createToken()
        if (!token) {
          return
        }
        await this.processToken(token)
      } finally {
        this.loaderPay = false
      }
    },
    /**
     * The single place the payment chain lives, shared by both experiences.
     */
    async processToken (token) {
      try {
        await this.createSource('mercadopago', { token: token })
        await this.authorizePayment()
        await this.capturePayment()
      } catch (error) {
        const code = this.gatewayErrorCode(error)
        if (code === 'P006') {
          this.processing = true
        } else if (['P004', 'P005'].includes(code)) {
          // Already resolved server side; PaymentRetrieve shows its real state.
          this.$router.push({ name: 'PaymentRetrieve' })
        } else {
          this.showError(this.resolveGatewayError(error))
        }
        return
      }

      // capture answers 200 even when the gateway declined: the serializer
      // persists status='failed' and returns without raising.
      if (this.paymentsDetail.status === 'failed') {
        this.showError(this.failedReasonMessage(this.paymentsDetail.failed_reason))
        this.leave({ name: 'PaymentError', params: { id: this.payment.id } })
        return
      }
      this.leave({ name: 'PaymentRetrieve' })
    },
    /**
     * Navigating tears this view down, and with it the child that is still
     * unwinding the call that got us here. Deferring by a macrotask lets pay()
     * finish resetting the button before the component is destroyed.
     */
    leave (route) {
      setTimeout(() => {
        this.$router.push(route)
      }, 0)
    }
  }
}
</script>
<style scoped>
/* Holds the room the form is about to take, so the Pagar button is not left
   alone against an empty card while the Brick renders. */
.mp-form--loading {
  min-height: 280px;
  position: relative;
}
.mp-form__skeleton {
  background-color: transparent;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}
.mp-form__content--loading {
  opacity: 0;
  pointer-events: none;
}
</style>
